'use client';

import * as React from 'react';
import { Button, Modal, SearchField, toast } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { IconUpload } from '@tabler/icons-react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { AssetImageMasonryPanel } from './AssetImageMasonryPanel';
import { UploadAssetModal } from './UploadAssetModal';
import {
  MEDIA_TYPE_OPTIONS,
  MEDIA_TYPE_SEARCH_PLACEHOLDER,
  SCOPE_OPTIONS,
} from './constants';
import type {
  AssetMediaItem,
  AssetMediaType,
  AssetScope,
  AssetSelectorModalProps,
  SelectedAsset,
} from './types';

function resolveInitialMediaType(allowedTypes?: AssetMediaType[]): AssetMediaType {
  if (!allowedTypes?.length) return 'image';
  return allowedTypes.includes('image') ? 'image' : allowedTypes[0]!;
}

function toSelectedAsset(item: AssetMediaItem): SelectedAsset {
  return {
    id: String(item.id),
    name: item.name,
    url: item.url,
    type: item.type,
    mimeType: item.mimeType,
    fileSize: item.fileSize,
  };
}

export function AssetSelectorModal({
  open,
  onOpenChange,
  title = '选择资产',
  maxCount,
  allowedTypes,
  onConfirm,
}: AssetSelectorModalProps) {
  const [scope, setScope] = React.useState<AssetScope>('my');
  const [mediaType, setMediaType] = React.useState<AssetMediaType>(() =>
    resolveInitialMediaType(allowedTypes),
  );
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedMap, setSelectedMap] = React.useState<Map<string, SelectedAsset>>(() => new Map());
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const scrollRootRef = React.useRef<HTMLDivElement>(null);
  const [galleryRefreshKey, setGalleryRefreshKey] = React.useState(0);

  const visibleMediaTypes = React.useMemo(() => {
    if (!allowedTypes?.length) return MEDIA_TYPE_OPTIONS;
    const allowed = new Set(allowedTypes);
    return MEDIA_TYPE_OPTIONS.filter((o) => allowed.has(o.key));
  }, [allowedTypes]);

  const resetState = React.useCallback(() => {
    setScope('my');
    setMediaType(resolveInitialMediaType(allowedTypes));
    setSearchKeyword('');
    setSelectionMode(false);
    setSelectedMap(new Map());
    setUploadOpen(false);
  }, [allowedTypes]);

  React.useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  React.useEffect(() => {
    if (!allowedTypes?.length) return;
    if (!allowedTypes.includes(mediaType)) {
      setMediaType(resolveInitialMediaType(allowedTypes));
    }
  }, [allowedTypes, mediaType]);

  const handleScopeChange = React.useCallback((key: React.Key) => {
    const next = String(key) as AssetScope;
    if (next === 'my' || next === 'space' || next === 'system') {
      setScope(next);
      setSelectionMode(false);
      setSelectedMap(new Map());
    }
  }, []);

  const handleToggleSelect = React.useCallback(
    (item: AssetMediaItem) => {
      const id = String(item.id);
      setSelectedMap((prev) => {
        const next = new Map(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        if (maxCount != null && maxCount > 0 && next.size >= maxCount) {
          toast.warning(`最多只能选择 ${maxCount} 项`);
          return prev;
        }
        next.set(id, toSelectedAsset(item));
        return next;
      });
    },
    [maxCount],
  );

  const handleEnterSelectionMode = React.useCallback(() => {
    setSelectionMode(true);
  }, []);

  const handleCancelSelection = React.useCallback(() => {
    setSelectionMode(false);
    setSelectedMap(new Map());
  }, []);

  const handleConfirmSelection = React.useCallback(() => {
    const assets = [...selectedMap.values()];
    if (assets.length === 0) {
      toast.warning('请先选择资产');
      return;
    }
    onConfirm?.(assets);
    onOpenChange(false);
  }, [onConfirm, onOpenChange, selectedMap]);

  const selectedIds = React.useMemo(() => new Set(selectedMap.keys()), [selectedMap]);

  return (
    <>
      <Modal isOpen={open} onOpenChange={onOpenChange}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside">
            <Modal.Dialog className="z-[1200] flex h-[75vh] w-[65vw] max-h-[75vh] max-w-[min(65vw,calc(100vw-2rem))] min-w-0 flex-col gap-0 overflow-hidden p-0">
              <Modal.Header className="relative shrink-0 border-b px-6 py-4">
                <div className="flex min-h-10 items-center justify-between gap-4">
                  <Modal.Heading className="shrink-0 text-xl font-semibold">{title}</Modal.Heading>
                  <div className="pointer-events-none absolute inset-x-0 flex justify-center px-32">
                    <Segment
                      className="pointer-events-auto min-w-0 max-w-md"
                      selectedKey={scope}
                      onSelectionChange={handleScopeChange}
                    >
                      {SCOPE_OPTIONS.map((item) => (
                        <Segment.Item key={item.key} id={item.key}>
                          <Segment.Separator />
                          {item.label}
                        </Segment.Item>
                      ))}
                    </Segment>
                  </div>
                  <div className="w-[1px] shrink-0 opacity-0" aria-hidden />
                </div>
              </Modal.Header>

              <div className="flex shrink-0 items-center justify-between gap-4 px-6 py-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Segment
                    className="shrink-0"
                    selectedKey={mediaType}
                    onSelectionChange={(key) => {
                      const next = String(key) as AssetMediaType;
                      if (visibleMediaTypes.some((o) => o.key === next)) {
                        setMediaType(next);
                        setSearchKeyword('');
                        setSelectionMode(false);
                        setSelectedMap(new Map());
                      }
                    }}
                  >
                    {visibleMediaTypes.map((item) => (
                      <Segment.Item key={item.key} id={item.key}>
                        <Segment.Separator />
                        {item.label}
                      </Segment.Item>
                    ))}
                  </Segment>

                  <SearchField
                    aria-label={MEDIA_TYPE_SEARCH_PLACEHOLDER[mediaType]}
                    className="min-w-0 max-w-72 shrink"
                    variant="secondary"
                    value={searchKeyword}
                    onChange={setSearchKeyword}
                  >
                    <SearchField.Group className="w-full rounded-full">
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-full min-w-0"
                        placeholder={MEDIA_TYPE_SEARCH_PLACEHOLDER[mediaType]}
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>

                  <Button
                    isIconOnly
                    aria-label="上传"
                    variant="secondary"
                    onPress={() => setUploadOpen(true)}
                  >
                    <IconUpload className="size-5" stroke={1.5} />
                  </Button>
                </div>

                <div className="flex shrink-0 items-center gap-2 pe-2">
                  {selectionMode ? (
                    <>
                      <Button variant="secondary" onPress={handleCancelSelection}>
                        取消
                      </Button>
                      <Button variant="primary" onPress={handleConfirmSelection}>
                        确定
                      </Button>
                    </>
                  ) : (
                    <Button variant="primary" onPress={handleEnterSelectionMode}>
                      选择
                    </Button>
                  )}
                </div>
              </div>

              <Modal.Body className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                {mediaType === 'image' ? (
                  <div ref={scrollRootRef} className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">
                    <AssetImageMasonryPanel
                      key={`${scope}-${galleryRefreshKey}`}
                      active={open && mediaType === 'image'}
                      listHostOpen={open}
                      scope={scope}
                      searchKeyword={searchKeyword}
                      selectionMode={selectionMode}
                      selectedIds={selectedIds}
                      onToggleSelect={handleToggleSelect}
                      scrollRootRef={scrollRootRef}
                    />
                  </div>
                ) : (
                  <div className="text-muted-foreground flex min-h-0 flex-1 items-center justify-center text-sm">
                    {MEDIA_TYPE_OPTIONS.find((o) => o.key === mediaType)?.label ?? mediaType}内容区开发中
                  </div>
                )}
              </Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <UploadAssetModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        mediaType={mediaType}
        onUploaded={() => {
          if (scope === 'my') {
            setGalleryRefreshKey((k) => k + 1);
          }
        }}
      />
    </>
  );
}
